import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Plus, Trash2, ChevronDown, ChevronRight, X, Upload, MapPin, Sparkles,
  ChevronUp, Palette, ImageIcon, Wand2, Pipette, Camera, Pencil, Check
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { ImageLightbox } from './ImageLightbox';
import { AuthImage } from './AuthImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------------------------- MOOD BOARD ---------------------------- */
const MoodBoardSection = ({ projectId, department, token }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiCount, setAiCount] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [lightbox, setLightbox] = useState(null); // {idx}
  const fileRef = useRef(null);

  const fetchImages = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/mood-board?department=${department}`, { headers });
      setImages(r.data);
    } catch { /* noop */ }
  }, [projectId, department, headers]);
  useEffect(() => { fetchImages(); }, [fetchImages]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append('file', file);
        await axios.post(
          `${API}/projects/${projectId}/mood-board/upload?department=${department}`,
          form,
          { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
        );
      }
      toast.success(`Uploaded ${files.length} image(s)`);
      fetchImages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) { toast.error('Enter a prompt'); return; }
    setGenerating(true);
    try {
      await axios.post(`${API}/projects/${projectId}/mood-board/generate`,
        { department, prompt: aiPrompt, count: aiCount }, { headers });
      toast.success('Mood board image generated');
      setShowAI(false);
      setAiPrompt('');
      fetchImages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI generation failed');
    } finally { setGenerating(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this mood board image?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/mood-board/${id}`, { headers });
      fetchImages();
    } catch { toast.error('Delete failed'); }
  };

  const handleReorder = async (id, direction) => {
    try {
      await axios.post(`${API}/projects/${projectId}/mood-board/${id}/reorder`,
        { direction }, { headers });
      fetchImages();
    } catch { /* noop */ }
  };

  return (
    <Card className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] p-5" data-testid="mood-board-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[var(--cb-accent)]" />
          <h3 className="font-['DM_Serif_Display'] text-lg text-[var(--cb-text)]">Mood Board</h3>
          <Badge variant="outline" className="border-[var(--cb-border)] text-[var(--cb-text-sec)] text-xs">{images.length}</Badge>
        </div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
          <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="border-[var(--cb-border)] text-[var(--cb-text-sec)] hover:text-[var(--cb-text)]" data-testid="mood-upload-btn">
            <Upload className="h-3 w-3 mr-1" /> {uploading ? 'Uploading…' : 'Upload'}
          </Button>
          <Button size="sm" onClick={() => setShowAI(true)} className="bg-[var(--cb-accent)] hover:bg-[var(--cb-accent)] text-black" data-testid="mood-ai-btn">
            <Wand2 className="h-3 w-3 mr-1" /> AI Generate
          </Button>
        </div>
      </div>
      {images.length === 0 ? (
        <div className="text-center py-8 text-[var(--cb-text-sec)] text-sm">
          <ImageIcon className="h-8 w-8 mx-auto mb-2 text-[var(--cb-border)]" />
          No mood board images yet. Upload references or generate with AI.
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {images.map((img, idx) => (
            <div key={img.id} className="relative group/mb aspect-square rounded-lg overflow-hidden border border-[var(--cb-border)]" data-testid={`mood-img-${img.id}`}>
              <AuthImage path={img.storage_path}
                alt={img.caption || 'mood'} className="h-full w-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => setLightbox({ idx })} />
              {img.source === 'ai' && (
                <Badge className="absolute top-1 left-1 bg-[#4a9eff]/90 text-white text-[10px] border-0 px-1.5 py-0">AI</Badge>
              )}
              <div className="absolute top-1 right-1 flex flex-col gap-0.5 opacity-0 group-hover/mb:opacity-100 transition-opacity">
                <button onClick={() => handleDelete(img.id)} className="bg-red-500/90 text-white rounded h-5 w-5 flex items-center justify-center">
                  <X className="h-3 w-3" />
                </button>
              </div>
              <div className="absolute bottom-1 right-1 flex gap-0.5 opacity-0 group-hover/mb:opacity-100 transition-opacity">
                <button onClick={() => handleReorder(img.id, 'up')} disabled={idx === 0}
                  className="bg-[var(--cb-surface-h)]/90 text-[var(--cb-text)] rounded h-5 w-5 flex items-center justify-center disabled:opacity-30">
                  <ChevronUp className="h-3 w-3" />
                </button>
                <button onClick={() => handleReorder(img.id, 'down')} disabled={idx === images.length - 1}
                  className="bg-[var(--cb-surface-h)]/90 text-[var(--cb-text)] rounded h-5 w-5 flex items-center justify-center disabled:opacity-30">
                  <ChevronDown className="h-3 w-3" />
                </button>
              </div>
              {img.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent px-2 py-1 text-[10px] text-white truncate">
                  {img.caption}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {lightbox && (
        <ImageLightbox
          images={images.map(i => ({ path: i.storage_path, alt: i.caption || 'mood', caption: i.caption }))}
          initialIndex={lightbox.idx}
          onClose={() => setLightbox(null)}
        />
      )}

      <Dialog open={showAI} onOpenChange={setShowAI}>
        <DialogContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)]">
          <DialogHeader>
            <DialogTitle className="font-['DM_Serif_Display'] text-xl flex items-center gap-2">
              <Wand2 className="h-4 w-4 text-[var(--cb-accent)]" /> Generate Mood Board with AI
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Describe the mood / aesthetic *</label>
              <Textarea
                value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
                placeholder="e.g. Neo-noir Mumbai street at dusk, neon signs reflected in rain puddles, cinematic 35mm, anamorphic flare"
                className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] min-h-[100px]"
                data-testid="mood-ai-prompt"
              />
            </div>
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Number of images</label>
              <div className="flex gap-2">
                {[1, 2, 3].map(n => (
                  <button key={n} onClick={() => setAiCount(n)}
                    className={`h-9 w-9 rounded text-sm font-medium transition-colors ${aiCount === n ? 'bg-[var(--cb-accent)] text-black' : 'bg-[var(--cb-border)] text-[var(--cb-text-sec)]'}`}>
                    {n}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-[var(--cb-text-sec)] mt-1">Each image uses AI credits from your account.</p>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowAI(false)} className="border-[var(--cb-border)] text-[var(--cb-text)]">Cancel</Button>
              <Button onClick={handleAIGenerate} disabled={generating} className="bg-[var(--cb-accent)] hover:bg-[var(--cb-accent)] text-black" data-testid="mood-ai-generate-btn">
                {generating ? 'Generating…' : 'Generate'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

/* ---------------------------- COLOR PALETTE ---------------------------- */
const ColorPaletteSection = ({ projectId, department, token }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [palettes, setPalettes] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState('');
  const [colors, setColors] = useState([{ hex: 'var(--cb-accent)', label: '' }]);
  const [saving, setSaving] = useState(false);
  // Extract
  const extractRef = useRef(null);
  const [extracting, setExtracting] = useState(false);
  // AI suggest
  const [showAI, setShowAI] = useState(false);
  const [aiName, setAiName] = useState('AI Suggested Palette');
  const [aiTone, setAiTone] = useState('');
  const [aiSaving, setAiSaving] = useState(false);

  const fetchPalettes = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/palettes?department=${department}`, { headers });
      setPalettes(r.data);
    } catch { /* noop */ }
  }, [projectId, department, headers]);
  useEffect(() => { fetchPalettes(); }, [fetchPalettes]);

  const handleCreate = async () => {
    if (!name.trim()) { toast.error('Palette name required'); return; }
    if (!colors.length) { toast.error('Add at least one color'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/projects/${projectId}/palettes`,
        { department, name, colors, source: 'manual' }, { headers });
      toast.success('Palette saved');
      setShowNew(false);
      setName('');
      setColors([{ hex: 'var(--cb-accent)', label: '' }]);
      fetchPalettes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleExtract = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExtracting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(
        `${API}/projects/${projectId}/palettes/extract?department=${department}&name=${encodeURIComponent('From ' + file.name)}&k=6`,
        form, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Palette extracted from image');
      fetchPalettes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Extraction failed');
    } finally { setExtracting(false); e.target.value = ''; }
  };

  const handleAISuggest = async () => {
    setAiSaving(true);
    try {
      await axios.post(`${API}/projects/${projectId}/palettes/ai-suggest`,
        { department, name: aiName, tone: aiTone || null }, { headers });
      toast.success('AI palette generated');
      setShowAI(false);
      setAiTone('');
      fetchPalettes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI suggest failed');
    } finally { setAiSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this palette?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/palettes/${id}`, { headers });
      fetchPalettes();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <Card className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] p-5" data-testid="palette-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Palette className="h-4 w-4 text-[var(--cb-accent)]" />
          <h3 className="font-['DM_Serif_Display'] text-lg text-[var(--cb-text)]">Color Palettes</h3>
          <Badge variant="outline" className="border-[var(--cb-border)] text-[var(--cb-text-sec)] text-xs">{palettes.length}</Badge>
        </div>
        <div className="flex gap-2">
          <input ref={extractRef} type="file" accept="image/*" onChange={handleExtract} className="hidden" />
          <Button size="sm" variant="outline" onClick={() => extractRef.current?.click()} disabled={extracting}
            className="border-[var(--cb-border)] text-[var(--cb-text-sec)] hover:text-[var(--cb-text)]" data-testid="palette-extract-btn">
            <Pipette className="h-3 w-3 mr-1" /> {extracting ? 'Extracting…' : 'Extract from Image'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowAI(true)}
            className="border-[var(--cb-border)] text-[var(--cb-text-sec)] hover:text-[var(--cb-text)]" data-testid="palette-ai-btn">
            <Wand2 className="h-3 w-3 mr-1" /> AI Suggest
          </Button>
          <Button size="sm" onClick={() => setShowNew(true)} className="bg-[var(--cb-accent)] hover:bg-[var(--cb-accent)] text-black" data-testid="palette-new-btn">
            <Plus className="h-3 w-3 mr-1" /> New Palette
          </Button>
        </div>
      </div>

      {palettes.length === 0 ? (
        <div className="text-center py-8 text-[var(--cb-text-sec)] text-sm">
          <Palette className="h-8 w-8 mx-auto mb-2 text-[var(--cb-border)]" />
          No palettes yet. Create one, extract from an image, or let AI suggest based on script tone.
        </div>
      ) : (
        <div className="space-y-3">
          {palettes.map(p => (
            <div key={p.id} className="bg-[var(--cb-bg)] border border-[var(--cb-border)] rounded-lg p-3" data-testid={`palette-${p.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[var(--cb-text)]">{p.name}</span>
                  {p.source === 'ai' && <Badge className="bg-[#4a9eff]/90 text-white text-[10px] border-0 px-1.5">AI</Badge>}
                  {p.source === 'extract' && <Badge className="bg-[var(--cb-accent)]/90 text-black text-[10px] border-0 px-1.5">EXTRACTED</Badge>}
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)} className="h-6 w-6 p-0 text-[var(--cb-text-sec)] hover:text-red-400">
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(p.colors || []).map((c, i) => (
                  <div key={i} className="flex flex-col items-center" title={`${c.hex} ${c.label || ''}`}>
                    <div
                      className="h-12 w-12 rounded-md border border-[var(--cb-border)] cursor-pointer"
                      style={{ backgroundColor: c.hex }}
                      onClick={() => { navigator.clipboard.writeText(c.hex); toast.success(`Copied ${c.hex}`); }}
                    />
                    <span className="text-[10px] text-[var(--cb-text-sec)] mt-0.5 font-mono">{c.hex}</span>
                    {c.label && <span className="text-[10px] text-[var(--cb-text)] truncate max-w-[64px]">{c.label}</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New Palette Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)] max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-['DM_Serif_Display'] text-xl">New Color Palette</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Palette Name *</label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Hero Look — Night"
                className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]" data-testid="palette-name-input" />
            </div>
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Colors</label>
              <div className="space-y-2">
                {colors.map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="color" value={c.hex}
                      onChange={e => setColors(prev => prev.map((x, j) => j === i ? { ...x, hex: e.target.value } : x))}
                      className="h-9 w-9 bg-transparent border border-[var(--cb-border)] rounded cursor-pointer" />
                    <Input value={c.hex}
                      onChange={e => setColors(prev => prev.map((x, j) => j === i ? { ...x, hex: e.target.value } : x))}
                      className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] font-mono w-28" />
                    <Input value={c.label || ''} placeholder="Label (e.g. Hero Red)"
                      onChange={e => setColors(prev => prev.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                      className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)] flex-1" />
                    <Button variant="ghost" size="sm" onClick={() => setColors(prev => prev.filter((_, j) => j !== i))}
                      className="text-[var(--cb-text-sec)] hover:text-red-400">
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button variant="ghost" size="sm" onClick={() => setColors(prev => [...prev, { hex: 'var(--cb-text-sec)', label: '' }])}
                className="text-[var(--cb-accent)] mt-2">
                <Plus className="h-3 w-3 mr-1" /> Add Color
              </Button>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowNew(false)} className="border-[var(--cb-border)] text-[var(--cb-text)]">Cancel</Button>
              <Button onClick={handleCreate} disabled={saving} className="bg-[var(--cb-accent)] hover:bg-[var(--cb-accent)] text-black" data-testid="palette-save-btn">
                {saving ? 'Saving…' : 'Save Palette'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Suggest Dialog */}
      <Dialog open={showAI} onOpenChange={setShowAI}>
        <DialogContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)]">
          <DialogHeader>
            <DialogTitle className="font-['DM_Serif_Display'] text-xl flex items-center gap-2">
              <Wand2 className="h-4 w-4 text-[var(--cb-accent)]" /> AI Palette Suggestion
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Palette Name</label>
              <Input value={aiName} onChange={e => setAiName(e.target.value)}
                className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]" />
            </div>
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Tone / Direction (optional)</label>
              <Input value={aiTone} onChange={e => setAiTone(e.target.value)}
                placeholder="e.g. noir thriller, monsoon Mumbai, warm 70s romance"
                className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]" />
              <p className="text-[11px] text-[var(--cb-text-sec)] mt-1">AI reads your script and suggests 6 colors. Uses AI credits from your account.</p>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowAI(false)} className="border-[var(--cb-border)] text-[var(--cb-text)]">Cancel</Button>
              <Button onClick={handleAISuggest} disabled={aiSaving} className="bg-[var(--cb-accent)] hover:bg-[var(--cb-accent)] text-black" data-testid="palette-ai-generate-btn">
                {aiSaving ? 'Suggesting…' : 'Suggest Palette'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

/* ---------------------------- LOCATION GALLERY (unlimited nesting + reorder) ---------------------------- */

// Build a tree from flat rooms
const buildTree = (rooms) => {
  const root = {};
  for (const room of rooms) {
    const parts = (room.room_path || 'General').split('/').filter(Boolean);
    let cursor = root;
    parts.forEach((part, i) => {
      if (!cursor[part]) cursor[part] = { __items: [], __children: {} };
      if (i === parts.length - 1) cursor[part].__items = room.items;
      cursor = cursor[part].__children;
    });
  }
  return root;
};

function TreeNode({ name, node, parentPath, depth, projectId, token, onChange, onAddSub, onOpenLightbox }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [expanded, setExpanded] = useState(true);
  const hasChildren = Object.keys(node.__children || {}).length > 0;
  const items = node.__items || [];
  const fullPath = parentPath ? `${parentPath}/${name}` : name;

  const handleDelete = async (refId) => {
    if (!window.confirm('Remove this reference?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/gallery/${refId}`, { headers });
      onChange();
    } catch { toast.error('Failed'); }
  };
  const handleReorder = async (refId, direction) => {
    try {
      await axios.post(`${API}/projects/${projectId}/gallery/${refId}/reorder`, { direction }, { headers });
      onChange();
    } catch { /* noop */ }
  };

  return (
    <div className="space-y-2" style={{ marginLeft: depth > 0 ? '1rem' : 0 }}>
      <div className="flex items-center gap-2">
        <button onClick={() => setExpanded(e => !e)} className="text-[var(--cb-text-sec)] hover:text-[var(--cb-text)]">
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <span className="h-1.5 w-1.5 rounded-full bg-[var(--cb-accent)]" />
        <span className="text-sm text-[var(--cb-text)]">{name}</span>
        <Badge variant="outline" className="border-[var(--cb-border)] text-[var(--cb-text-sec)] text-[10px]">{items.length}</Badge>
        <Button variant="ghost" size="sm"
          onClick={() => onAddSub(fullPath)}
          className="h-6 px-2 text-xs text-[var(--cb-text-sec)] hover:text-[var(--cb-accent)]" data-testid={`add-sub-${fullPath}`}>
          <Plus className="h-3 w-3 mr-0.5" /> Subsection
        </Button>
      </div>
      {expanded && (
        <>
          {items.length > 0 && (
            <div className="flex flex-wrap gap-2 ml-6">
              {items.map((ref, idx) => (
                <div key={ref.id} className="relative group/ref" data-testid={`gallery-ref-${ref.id}`}>
                  <AuthImage
                    path={ref.storage_path}
                    alt={ref.original_filename}
                    className="h-24 w-24 object-cover rounded-lg border border-[var(--cb-border)] cursor-pointer hover:opacity-90 hover:border-[var(--cb-accent)] transition-all"
                    onClick={() => onOpenLightbox && onOpenLightbox(items, idx)}
                  />
                  <button onClick={() => handleDelete(ref.id)}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full h-4 w-4 flex items-center justify-center opacity-0 group-hover/ref:opacity-100 transition-opacity">
                    <X className="h-2.5 w-2.5" />
                  </button>
                  <div className="absolute bottom-1 right-1 flex gap-0.5 opacity-0 group-hover/ref:opacity-100 transition-opacity">
                    <button onClick={() => handleReorder(ref.id, 'up')} disabled={idx === 0}
                      className="bg-[var(--cb-surface-h)]/90 text-[var(--cb-text)] rounded h-5 w-5 flex items-center justify-center disabled:opacity-30">
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button onClick={() => handleReorder(ref.id, 'down')} disabled={idx === items.length - 1}
                      className="bg-[var(--cb-surface-h)]/90 text-[var(--cb-text)] rounded h-5 w-5 flex items-center justify-center disabled:opacity-30">
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {hasChildren && (
            <div className="ml-3 border-l border-[var(--cb-border)] pl-3 space-y-2">
              {Object.entries(node.__children).map(([childName, childNode]) =>
                React.createElement(TreeNode, {
                  key: childName,
                  name: childName,
                  node: childNode,
                  parentPath: fullPath,
                  depth: depth + 1,
                  projectId,
                  token,
                  onChange,
                  onAddSub,
                  onOpenLightbox
                })
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const LocationGallery = ({ projectId, department, token }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [data, setData] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [locName, setLocName] = useState('');
  const [pathInput, setPathInput] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [expandedLocs, setExpandedLocs] = useState({});
  const [renamingLoc, setRenamingLoc] = useState(null); // location_name being renamed
  const [renameText, setRenameText] = useState('');
  const [galleryLightbox, setGalleryLightbox] = useState(null); // {images, idx}
  const fileRef = useRef(null);

  const handleRename = async (oldName) => {
    if (!renameText.trim() || renameText.trim() === oldName) { setRenamingLoc(null); return; }
    try {
      await axios.patch(`${API}/projects/${projectId}/gallery/rename-location`,
        { old_name: oldName, new_name: renameText.trim() }, { headers });
      toast.success(`Renamed to "${renameText.trim()}"`);
      setRenamingLoc(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rename failed'); }
  };

  const fetchData = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/gallery?department=${department}`, { headers });
      setData(r.data);
    } catch { /* noop */ }
  }, [projectId, department, headers]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async () => {
    if (!locName.trim()) { toast.error('Location required'); return; }
    if (!files.length) { toast.error('Choose at least one image'); return; }
    setUploading(true);
    try {
      const params = new URLSearchParams({
        department,
        location_name: locName,
        room_path: pathInput || 'General'
      });
      for (const f of files) {
        const form = new FormData();
        form.append('file', f);
        await axios.post(`${API}/projects/${projectId}/gallery?${params.toString()}`, form,
          { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(`Uploaded ${files.length} image(s)`);
      setShowUpload(false);
      setFiles([]);
      setLocName('');
      setPathInput('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const openAddSub = (locationName, parentPath = '') => {
    setLocName(locationName);
    setPathInput(parentPath ? `${parentPath}/` : '');
    setFiles([]);
    setShowUpload(true);
  };

  return (
    <Card className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] p-5" data-testid="location-gallery-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-[var(--cb-accent)]" />
          <h3 className="font-['DM_Serif_Display'] text-lg text-[var(--cb-text)]">Look &amp; Feel — Locations</h3>
          <Badge variant="outline" className="border-[var(--cb-border)] text-[var(--cb-text-sec)] text-xs">{data.length}</Badge>
        </div>
        <Button size="sm" onClick={() => { setLocName(''); setPathInput(''); setFiles([]); setShowUpload(true); }}
          className="bg-[var(--cb-accent)] hover:bg-[var(--cb-accent)] text-black" data-testid="gallery-add-btn">
          <Plus className="h-3 w-3 mr-1" /> Add References
        </Button>
      </div>

      {data.length === 0 ? (
        <div className="text-center py-8 text-[var(--cb-text-sec)] text-sm">
          <MapPin className="h-8 w-8 mx-auto mb-2 text-[var(--cb-border)]" />
          No location references yet. Add photos for each set, organized by location and subsection.
        </div>
      ) : (
        <div className="space-y-2">
          {data.map(locGroup => {
            const tree = buildTree(locGroup.rooms);
            const isExpanded = expandedLocs[locGroup.location_name] !== false;
            return (
              <div key={locGroup.location_name} className="bg-[var(--cb-bg)] border border-[var(--cb-border)] rounded-lg overflow-hidden"
                data-testid={`gallery-loc-${locGroup.location_name}`}>
                <button
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-[var(--cb-surface-h)] transition-colors"
                  onClick={() => setExpandedLocs(p => ({ ...p, [locGroup.location_name]: !isExpanded }))}
                >
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-[var(--cb-accent)]" />
                    {renamingLoc === locGroup.location_name ? (
                      <span onClick={e => e.stopPropagation()} className="flex items-center gap-1">
                        <input
                          autoFocus
                          value={renameText}
                          onChange={e => setRenameText(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleRename(locGroup.location_name); if (e.key === 'Escape') setRenamingLoc(null); }}
                          className="bg-[var(--cb-border)] border border-[var(--cb-accent)]/50 rounded px-1.5 py-0.5 text-sm text-[var(--cb-text)] outline-none w-40"
                          data-testid={`gallery-loc-rename-input-${locGroup.location_name}`}
                        />
                        <button onClick={e => { e.stopPropagation(); handleRename(locGroup.location_name); }} className="text-[#4ade80] hover:text-green-300">
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={e => { e.stopPropagation(); setRenamingLoc(null); }} className="text-[var(--cb-text-sec)] hover:text-red-400">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    ) : (
                      <>
                        <span className="font-medium text-[var(--cb-text)] text-sm">{locGroup.location_name}</span>
                        <span className="text-xs text-[var(--cb-text-sec)]">({locGroup.total} images)</span>
                        <button
                          onClick={e => { e.stopPropagation(); setRenamingLoc(locGroup.location_name); setRenameText(locGroup.location_name); }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-[var(--cb-text-sec)] hover:text-[var(--cb-accent)]"
                          data-testid={`gallery-loc-rename-btn-${locGroup.location_name}`}
                          title="Rename location"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); openAddSub(locGroup.location_name); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); openAddSub(locGroup.location_name); } }}
                      className="text-xs text-[var(--cb-accent)] hover:underline px-2 py-1 inline-flex items-center"
                      data-testid={`gallery-add-sub-${locGroup.location_name}`}
                    >
                      <Plus className="h-3 w-3 mr-1" /> Subsection
                    </span>
                    {isExpanded ? <ChevronDown className="h-4 w-4 text-[var(--cb-text-sec)]" /> : <ChevronRight className="h-4 w-4 text-[var(--cb-text-sec)]" />}
                  </div>
                </button>
                {isExpanded && (
                  <div className="px-3 pb-3 pt-1 space-y-2">
                    {Object.entries(tree).map(([rootName, rootNode]) => (
                      <TreeNode
                        key={rootName}
                        name={rootName}
                        node={rootNode}
                        parentPath=""
                        depth={0}
                        projectId={projectId}
                        token={token}
                        onChange={fetchData}
                        onAddSub={(parentPath) => openAddSub(locGroup.location_name, parentPath)}
                        onOpenLightbox={(items, idx) => setGalleryLightbox({
                          images: items.map(r => ({ path: r.storage_path, alt: r.original_filename })),
                          idx
                        })}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="bg-[var(--cb-surface-h)] border-[var(--cb-border)] text-[var(--cb-text)]">
          <DialogHeader>
            <DialogTitle className="font-['DM_Serif_Display'] text-xl">Add Reference Images</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Location / Set *</label>
              <Input value={locName} onChange={e => setLocName(e.target.value)} placeholder="e.g. Police Precinct"
                className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]" data-testid="gallery-loc-input" />
            </div>
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-1 block">Subsection Path (optional)</label>
              <Input value={pathInput} onChange={e => setPathInput(e.target.value)}
                placeholder="e.g. Ground Floor/Kitchen/Counter (use / to nest)"
                className="bg-[var(--cb-bg)] border-[var(--cb-border)] text-[var(--cb-text)]" data-testid="gallery-path-input" />
              <p className="text-[11px] text-[var(--cb-text-sec)] mt-1">Use slashes to create unlimited nested subsections.</p>
            </div>
            <div>
              <label className="text-xs text-[var(--cb-text-sec)] mb-2 block">Images * (multiple allowed)</label>
              <input ref={fileRef} type="file" accept="image/*" multiple
                onChange={e => setFiles(Array.from(e.target.files || []))} className="hidden" />
              <button onClick={() => fileRef.current?.click()}
                className="h-24 w-full border-2 border-dashed border-[var(--cb-border)] rounded-lg flex flex-col items-center justify-center gap-2 hover:border-[var(--cb-accent)] transition-colors"
                data-testid="gallery-file-trigger">
                {files.length > 0 ? (
                  <><Camera className="h-5 w-5 text-[var(--cb-accent)]" /><span className="text-sm text-[var(--cb-text)]">{files.length} file(s) selected</span></>
                ) : (
                  <><Upload className="h-5 w-5 text-[var(--cb-text-sec)]" /><span className="text-sm text-[var(--cb-text-sec)]">Click to choose images</span></>
                )}
              </button>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowUpload(false)} className="border-[var(--cb-border)] text-[var(--cb-text)]">Cancel</Button>
              <Button onClick={handleUpload} disabled={uploading} className="bg-[var(--cb-accent)] hover:bg-[var(--cb-accent)] text-black" data-testid="gallery-upload-btn">
                {uploading ? 'Uploading…' : `Upload ${files.length || ''} Reference${files.length !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {galleryLightbox && (
        <ImageLightbox
          images={galleryLightbox.images}
          initialIndex={galleryLightbox.idx}
          onClose={() => setGalleryLightbox(null)}
        />
      )}
    </Card>
  );
};

/* ---------------------------- WRAPPER ---------------------------- */
const LookAndFeelGallery = ({ projectId, department }) => {
  const { token } = useAuth();
  return (
    <div className="space-y-5" data-testid="look-and-feel-gallery">
      <MoodBoardSection projectId={projectId} department={department} token={token} />
      <ColorPaletteSection projectId={projectId} department={department} token={token} />
      <LocationGallery projectId={projectId} department={department} token={token} />
    </div>
  );
};

export default LookAndFeelGallery;
